import { useEffect, useState } from 'react'
import { FaLayerGroup } from 'react-icons/fa'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'

/**
 * Funded claims broken down by approved-lender category (funder_defendants.category).
 * Backed by POST /api/approved-lenders-category-stats; pass `batchGroup` to
 * scope to one funding batch, omit it for every active batch reference.
 */

export interface CategoryStatRow {
    category: number | string | null
    claims: number
    clients: number
    batches: number
    value: number
}

export interface CategoryStatsResponse {
    success: boolean
    batchGroup: string | null
    categories: CategoryStatRow[]
    totals: { claims: number; clients: number; value: number }
}

const gbp = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })
const int = new Intl.NumberFormat('en-GB')

export const categoryLabel = (category: CategoryStatRow['category']): string =>
    category == null || category === '' ? 'Uncategorised' : `Category ${category}`

const pct = (part: number, whole: number): string => (whole > 0 ? `${((part / whole) * 100).toFixed(1)}%` : '—')

interface LenderCategoryStatsProps {
    batchGroup?: string | null
    title?: string
    compact?: boolean
    /** Bump to force a reload (e.g. after toggling a lender). */
    refreshKey?: number
}

export const LenderCategoryStats = ({ batchGroup = null, title, compact = false, refreshKey = 0 }: LenderCategoryStatsProps) => {
    const [data, setData] = useState<CategoryStatsResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        let cancelled = false
        const load = async () => {
            setLoading(true)
            setError(null)
            try {
                const response = await api({
                    url: '/approved-lenders-category-stats',
                    data: batchGroup ? { batchGroup } : {},
                })
                if (!cancelled) {
                    if (response.data?.success) setData(response.data)
                    else setError(response.data?.message || 'Could not load category statistics.')
                }
            } catch (err: any) {
                if (!cancelled) setError(err?.response?.data?.message || 'Could not load category statistics.')
            } finally {
                if (!cancelled) setLoading(false)
            }
        }
        void load()
        return () => {
            cancelled = true
        }
    }, [batchGroup, refreshKey])

    const heading = title ?? (batchGroup ? 'Claims by lender category' : 'Funded claims by lender category')

    return (
        <section className={`${styles.card} ${compact ? styles.compact : ''}`}>
            <header className={styles.header}>
                <h3>
                    <FaLayerGroup /> {heading}
                </h3>
                <span className={styles.scope}>{batchGroup ? `Batch ${batchGroup}` : 'All active batch references'}</span>
            </header>

            {loading && <div className={styles.state}>Loading category breakdown...</div>}
            {!loading && error && <div className={`${styles.state} ${styles.error}`}>{error}</div>}
            {!loading && !error && data && data.categories.length === 0 && (
                <div className={styles.state}>No funded claims found.</div>
            )}

            {!loading && !error && data && data.categories.length > 0 && (
                <>
                    <div className={styles.grid}>
                        {data.categories.map((row) => (
                            <div key={String(row.category ?? 'null')} className={styles.stat}>
                                <span className={styles.statLabel}>{categoryLabel(row.category)}</span>
                                <span className={styles.statNumber}>{int.format(row.claims)}</span>
                                <span className={styles.statSub}>
                                    {pct(row.claims, data.totals.claims)} of claims · {int.format(row.clients)} clients
                                </span>
                                <span className={styles.statSub}>{gbp.format(row.value)}</span>
                                {!batchGroup && <span className={styles.statSub}>{int.format(row.batches)} batches</span>}
                            </div>
                        ))}
                        <div className={`${styles.stat} ${styles.total}`}>
                            <span className={styles.statLabel}>Total</span>
                            <span className={styles.statNumber}>{int.format(data.totals.claims)}</span>
                            <span className={styles.statSub}>{int.format(data.totals.clients)} clients</span>
                            <span className={styles.statSub}>{gbp.format(data.totals.value)}</span>
                        </div>
                    </div>
                    {data.categories.some((row) => row.category == null) && (
                        <p className={styles.footnote}>
                            Uncategorised claims carry a defendant name that does not resolve to a row in the
                            approved lender list (usually spreadsheet imports). Add the name as an alias in
                            Admin &gt; Approved Lenders to categorise them.
                        </p>
                    )}
                </>
            )}
        </section>
    )
}
